import type {
  VehicleSpecsDTO,
  VehicleDTO,
  LegacyVehicleDTOExtended,
  NewVehicleDTO,
  LoanDTO,
  VehicleLoanDto,
  OwnershipResponseDto,
  OwnershipCreateRequest
} from '@inside-hub-app/customer-portal-shared'
import { VehicleStatus } from '@inside-hub-app/customer-portal-shared'
import { type EquipmentDTO } from '../services/vehicles.service'

/**
 * Maps a single ownership item from GET /v1/ownerships
 * (OwnershipResponseDto) to the shape the app consumes (NewVehicleDTO).
 *
 * Fields not present here (fuelType, details, loan, leasingExpiryDate,
 * nextServiceDate) are backfilled later from the dedicated endpoints
 * (vehicle details / loan / ownership details).
 *
 * `firstRegistration` is only returned by the ownership API when CPT holds the value
 * (e.g. portal-created vehicles); otherwise it is backfilled from vehicle details.
 * It stays the BE's date-only string (yyyy-MM-dd) — never converted to ISO.
 */
export function fromOwnershipResponseToVehicleDTO (
  item: OwnershipResponseDto
): NewVehicleDTO {
  return {
    ownershipId: item.id,
    vin: item.vin,
    nickname: item.nickname,
    active: item.status?.toLowerCase() === VehicleStatus.ACTIVE,
    requestedForRemoval: item.requestedForRemoval,
    deactivateRequestDate: item.deactivateRequestDate,
    brand: item.model?.brand?.name,
    model: item.model?.name,
    description: item.description,
    currentMileage: item.currentMileage,
    manualMileage: item.manualMileage,
    licensePlate: item.registrationPlate,
    firstRegistration: item.firstRegistration,
    imageUrl: item.imageUrl,
    createdByDataSource: item.createdByDataSource,
    dateCreated: item.dateCreated,
    dateModified: item.dateModified,
    dealerId: item.dealer?.id,
    business: item.companyOwnership,
    vehicleCompany: {
      name: item.owner?.fullName
    }
  }
}

export function fromNewVehicleDtoToOwnershipRequest (
  vehicle: NewVehicleDTO
): OwnershipCreateRequest {
  return {
    vin: vehicle.vin,
    brand: {
      name: vehicle.brand
    },
    model: {
      id: vehicle.modelId,
      name: vehicle.model
    },
    description: vehicle.description,
    registrationPlate: vehicle.licensePlate,
    firstRegistration: vehicle.firstRegistration ? new Date(vehicle.firstRegistration) : null,
    leasingExpiryDate: vehicle.leasingExpiryDate ? new Date(vehicle.leasingExpiryDate) : null,
    nextServiceDate: vehicle.nextServiceDate ? new Date(vehicle.nextServiceDate) : null,
    manualMileage: vehicle.manualMileage
  }
}

export function fromLegacyVehicleDetailsToVehicleSpecsDTO (
  vehicle: VehicleDTO | LegacyVehicleDTOExtended
): VehicleSpecsDTO {
  const d = vehicle.details

  return {
    engine: {
      fuel: {
        code: d?.fuelTypeNormalized,
        name: vehicle?.fuelType ?? d?.fuelType
      }
    },

    transmission: {
      code: d?.transmissionTypeNormalized,
      name: d?.transmissionType
    },

    driveType: {
      code: d?.driveTypeNormalized,
      name: d?.driveType
    },

    power: {
      ps: d?.enginePower
    },

    environment: {
      co2Emission: d?.co2Emission,
      emissionClass: d?.energyLabel,
      consumption: {
        combined: d?.consumptionCombined,
        urban: d?.consumptionUrban
      }
    },

    appearance: {
      name: d?.exteriorColorDisplayName ?? d?.exteriorColor,
      code: d?.exteriorColorCode,
      hexCode: d?.exteriorColorHexCode
    },

    equipment:
      d?.equipmentDetails?.map(e => ({
        type: e.type,
        name: e.name
      })) ?? [],

    licencePlate: vehicle.licensePlate,
    firstRegistration: vehicle.firstRegistration
  }
}

export function fromVehicleSpecsDTOToLegacyVehicle (
  vehicle: NewVehicleDTO
): VehicleDTO {
  const specs = vehicle.details

  const legacyDetails = {
    fuelType: specs?.engine?.fuel?.name,
    fuelTypeNormalized: specs?.engine?.fuel?.code,
    transmissionType: specs?.transmission?.name,
    transmissionTypeNormalized: specs?.transmission?.code,
    driveType: specs?.driveType?.name,
    driveTypeNormalized: specs?.driveType?.code,
    enginePower: specs?.power?.ps,
    co2Emission: specs?.environment?.co2Emission,
    energyLabel: specs?.environment?.emissionClass,
    consumptionCombined: specs?.environment?.consumption?.combined,
    consumptionUrban: specs?.environment?.consumption?.urban,
    exteriorColorDisplayName: specs?.appearance?.name,
    exteriorColorCode: specs?.appearance?.code,
    exteriorColorHexCode: specs?.appearance?.hexCode,
    equipment: specs?.equipment?.map(e => e.name) ?? [],
    equipmentDetails:
      specs?.equipment?.map(
        (e): EquipmentDTO => ({
          type: e.type,
          name: e.name
        })
      ) ?? [],
    licencePlate: specs?.licencePlate,
    firstRegistration: specs?.firstRegistration
      ? new Date(specs.firstRegistration)
      : undefined
  }

  return {
    ...vehicle,
    fuelType: specs?.engine?.fuel?.name,
    details: legacyDetails
  }
}

export function fromLegacyLoanToVehicleLoan (
  loan?: LoanDTO
): VehicleLoanDto | undefined {
  if (loan == null) {
    return undefined
  }

  return {
    number: loan.multileaseContractId,
    type: loan.type,
    providerName: loan.providerName,
    startDate: loan.startDate,
    endDate: loan.endDate,
    kmLimit: loan.kmLimit,
    costPerKm: loan.costPerKm,
    monthlyRate: loan.monthlyRate,
    residualValue: loan.residualValue,
    remainingValue: loan.remainingValue,
    months: loan.durationMonth,
    source: loan.allowContact === true ? 'Multilease' : undefined
  }
}
